import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';

class FeaturedMerch extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.visibilityObserver = null;
    this.visibleSlides = new Set();
  }

  connectedCallback() {
    const context = this;

    this.swiper = new Swiper(this.querySelector('.swiper'), {
      modules: [Navigation, Pagination],
      centeredSlides: false,
      spaceBetween: 8,
      loop: false,
      slidesPerView: 'auto',
      watchSlidesProgress: true, // Enables swiper-slide-visible class
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      on: {
        init: function () {
          // Set up IntersectionObserver after Swiper initializes
          requestAnimationFrame(() => {
            context.setupVisibilityObserver();
          });
        },
        slideChangeTransitionEnd: function () {
          // Swiper events provide immediate feedback while observer catches edge cases
          context.updateSlideFocusabilityFromSet();
        },
        resize: function () {
          context.updateSlideFocusabilityFromSet();
        },
      },
    });
  }

  disconnectedCallback() {
    // Clean up observer when element is removed from DOM
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
      this.visibilityObserver = null;
    }
    this.visibleSlides.clear();
  }

  /**
   * Set up IntersectionObserver for reliable visibility detection.
   * This avoids timing issues with Swiper's swiper-slide-visible class
   * which may not be applied correctly on init with slidesPerView: 'auto'.
   * WCAG 2.4.3 (Focus Order) compliance - FOXH-166
   */
  setupVisibilityObserver() {
    if (!this.swiper) return;

    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) return;

    // Clean up existing observer if present
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    const context = this;

    this.visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          // Consider slide visible if at least 10% is showing
          if (entry.isIntersecting && entry.intersectionRatio > 0.1) {
            context.visibleSlides.add(slide);
          } else {
            context.visibleSlides.delete(slide);
          }
        });

        // Update accessibility for all slides based on visibility
        context.updateSlideFocusabilityFromSet();
      },
      {
        root: swiperContainer,
        threshold: [0, 0.1, 0.5, 1],
        rootMargin: '0px',
      }
    );

    // Observe all slides
    const slides = this.swiper.slides;
    if (slides && slides.length) {
      slides.forEach((slide) => {
        this.visibilityObserver.observe(slide);
      });
    }

    // Initial update
    this.updateSlideFocusabilityFromSet();
  }

  /**
   * Updates focusability of slides using the IntersectionObserver's visibility Set.
   * Only visible slides are tabbable; hidden slides get aria-hidden and tabindex="-1".
   * WCAG 2.4.3 (Focus Order) compliance - FOXH-166
   */
  updateSlideFocusabilityFromSet() {
    if (!this.swiper) return;

    const slides = this.swiper.slides;
    if (!slides || !slides.length) return;

    // Selector for all focusable elements
    const focusableSelectors =
      'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])';

    slides.forEach((slide) => {
      // Use the Set from IntersectionObserver, fallback to swiper-slide-visible class
      const isVisible =
        this.visibleSlides.has(slide) ||
        slide.classList.contains('swiper-slide-visible');

      // Get all focusable elements inside the slide
      const focusableElements = slide.querySelectorAll(focusableSelectors);

      if (isVisible) {
        // Visible slide: remove aria-hidden, restore tabindex
        slide.removeAttribute('aria-hidden');

        // Restore slide's own tabindex
        const prevTabindex = slide.getAttribute('data-prev-tabindex');
        if (prevTabindex !== null) {
          if (prevTabindex === '') {
            slide.removeAttribute('tabindex');
          } else {
            slide.setAttribute('tabindex', prevTabindex);
          }
          slide.removeAttribute('data-prev-tabindex');
        } else if (slide.getAttribute('tabindex') === '-1') {
          slide.removeAttribute('tabindex');
        }

        // Restore focusable elements inside the slide
        focusableElements.forEach((el) => {
          const elPrevTabindex = el.getAttribute('data-prev-tabindex');
          if (elPrevTabindex !== null) {
            if (elPrevTabindex === '') {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', elPrevTabindex);
            }
            el.removeAttribute('data-prev-tabindex');
          } else if (el.getAttribute('tabindex') === '-1') {
            el.removeAttribute('tabindex');
          }
          el.removeAttribute('aria-hidden');
        });
      } else {
        // Hidden slide: hide from assistive tech, make unfocusable
        slide.setAttribute('aria-hidden', 'true');

        // Save and disable slide's own tabindex
        const currentTabindex = slide.getAttribute('tabindex');
        if (currentTabindex !== '-1') {
          slide.setAttribute(
            'data-prev-tabindex',
            currentTabindex !== null ? currentTabindex : ''
          );
          slide.setAttribute('tabindex', '-1');
        }

        // Disable focusable elements inside the slide
        focusableElements.forEach((el) => {
          const elCurrentTabindex = el.getAttribute('tabindex');
          if (elCurrentTabindex !== '-1') {
            el.setAttribute(
              'data-prev-tabindex',
              elCurrentTabindex !== null ? elCurrentTabindex : ''
            );
            el.setAttribute('tabindex', '-1');
          }
        });
      }
    });
  }
}

customElements.define('featured-merch', FeaturedMerch);
